import { colorSurfacePrimary } from 'component/color';
import { UtilityText } from 'component/text';
import { useEffect, useState } from 'react';
import { View } from 'react-native';

export function useCountdown(timer = 60, interval = 1000) {
    const [count, setCount] = useState(timer);
    const [countdownFinished, setCountDownFinished] = useState(false);

    useEffect(() => {
        if (process.env.NODE_ENV === 'test') {
            return;
        }

        if (count <= 0) {
            setCount(0);
            setCountDownFinished(true);
            return;
        }

        const timerId = setInterval(() => {
            setCount((prevCount) => prevCount - interval / 1000);
        }, interval);

        return () => clearInterval(timerId);
    }, [count]);

    // HACK: countdown causes test randomness, so we skip it in test mode
    if (process.env.NODE_ENV === 'test') {
        return { count: timer, countdownFinished: false, setCount: () => {} };
    }

    return { count, countdownFinished, setCount };
}

export type VideoVotingCountdownElementProps = {
    size?: number;
    circleRadius?: number;
    strokeColor?: string;
    strokeBackgroundColor?: string;
    backgroundColor?: string;
    strokeWidth?: number;
    timer: number;
    callback: any;
};

/**
 * A circular countdown element which runs down in the given time and executes a callback
 */
export function VideoVotingCountdownElement({
    size = 48,
    circleRadius = 18,
    strokeColor = '#484848',
    strokeBackgroundColor = '#00000040',
    backgroundColor = colorSurfacePrimary,
    strokeWidth = 4,
    timer,
    callback,
}: VideoVotingCountdownElementProps) {
    const { count, countdownFinished, setCount } = useCountdown(timer, 100);
    useEffect(() => {
        if (!countdownFinished) return;
        if (callback) {
            callback();
        }
    }, [countdownFinished]);

    useEffect(() => {
        setCount(timer);
    }, [timer]);

    return (
        <View style={{ backgroundColor: backgroundColor, borderRadius: circleRadius * 2 }}>
            <CircularBar
                circleRadius={circleRadius}
                percentage={(count * 100) / timer}
                size={size}
                strokeBackgroundColor={strokeBackgroundColor}
                strokeColor={strokeColor}
                strokeWidth={strokeWidth}
            >
                <UtilityText text={Math.ceil(count).toString()}></UtilityText>
            </CircularBar>
        </View>
    );
}

export type CircularBarProps = {
    children: React.ReactNode;
    size?: number;
    circleRadius?: number;
    strokeColor?: string;
    strokeBackgroundColor?: string;
    strokeWidth?: number;
    percentage?: number;
};

/* eslint-disable-next-line max-lines-per-function */

// FIXME: This function is too large and should be broken up
export function CircularBar({
    children,
    size = 50,
    circleRadius = 20,
    strokeColor = '#484848',
    strokeBackgroundColor = '#00000040',
    strokeWidth = 5,
    percentage = 100,
}: CircularBarProps) {
    const radius = circleRadius;
    const circumference = 2 * Math.PI * radius;
    const arcLength = (percentage / 100) * circumference;

    return (
        <View style={[{ width: size, height: size }]}>
            <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>{children}</View>
            <View style={{ position: 'absolute' }}>
                <svg height={size} viewBox={`0 0 ${size} ${size}`} width={size}>
                    <path
                        d={`
                            m ${size / 2},${size / 2 - radius}
                            a ${radius},${radius},0,0,0,0,${2 * radius}
                            a ${-radius},${-radius},0,0,0,0,${-2 * radius}
                            z
                        `}
                        fill='none'
                        stroke={strokeBackgroundColor}
                        strokeLinecap='butt'
                        strokeWidth={strokeWidth}
                    />
                    <path
                        d={`
                            m ${size / 2},${size / 2 - radius}
                            a ${radius},${radius},0,0,0,0,${2 * radius}
                            a ${-radius},${-radius},0,0,0,0,${-2 * radius}
                            z
                        `}
                        fill='none'
                        stroke={strokeColor}
                        strokeDasharray={`${arcLength}, ${circumference}`}
                        strokeLinecap='butt'
                        strokeWidth={strokeWidth}
                    />
                </svg>
            </View>
        </View>
    );
}
